// Fallbacks: what a message says when a contact is missing the value, so nobody gets "Hi ,"
// (TRK-5064). The engine reads them inline as `{{contact.firstName ?? "there"}}` — the `| default:`
// pipe renders EMPTY and silently (measured on dev 08-18), so `??` is the only syntax written here.
// Keeping the fallback inside the text keeps a message portable.

/** A token that carries no fallback, e.g. `{{contact.firstName}}`. */
const PLAIN_TOKEN = /\{\{\s*([\w.]+)\s*\}\}/g

/** A token with a fallback, e.g. `{{contact.firstName ?? "there"}}`. Single or double quotes; a
 *  backslash escapes the closing quote, matching the API's parser. */
const TOKEN_WITH_FALLBACK = /\{\{\s*([\w.]+)\s*\?\?\s*(?:"((?:[^"\\]|\\.)*)"|'((?:[^'\\]|\\.)*)')\s*\}\}/g

const unescape = (text: string): string => text.replace(/\\(["'\\])/g, `$1`)
const escape = (text: string): string => text.replace(/(["\\])/g, `\\$1`)

/** One run of message text: either literal text or a token (with its full raw source, so a chip
 *  can carry the fallback syntax around invisibly and reassembly is lossless). */
export type TokenSegment = { kind: `text`; text: string } | { kind: `token`; raw: string; token: string }

/** Split message text into literal runs and tokens, in order. The chip editor renders tokens as
 *  atomic chips and never shows the raw `{{contact.firstName ?? "there"}}` syntax; joining every
 *  segment's raw/text back together reproduces the input exactly. */
export function parseTokenSegments(text: string): TokenSegment[] {
  const scanner = new RegExp(`${TOKEN_WITH_FALLBACK.source}|${PLAIN_TOKEN.source}`, `g`)
  const segments: TokenSegment[] = []
  let cursor = 0
  for (const match of text.matchAll(scanner)) {
    const index = match.index ?? 0
    if (index > cursor) segments.push({ kind: `text`, text: text.slice(cursor, index) })
    // Groups 1-3 belong to TOKEN_WITH_FALLBACK, group 4 to PLAIN_TOKEN.
    segments.push({ kind: `token`, raw: match[0], token: match[1] ?? match[4] })
    cursor = index + match[0].length
  }
  if (cursor < text.length) segments.push({ kind: `text`, text: text.slice(cursor) })
  return segments
}

/** The friendly reading of message text: tokens become their labels ("Hi First name, ...").
 *  For summaries (node cards) where the raw syntax would leak plumbing into the canvas. */
export function humanizeTokens(text: string, label: (token: string) => string): string {
  return parseTokenSegments(text)
    .map((segment) => (segment.kind === `token` ? label(segment.token) : segment.text))
    .join(``)
}

/** Every token the text uses, in the order they appear, without duplicates. */
export function tokensUsed(text: string): string[] {
  const found: string[] = []
  for (const [, token] of text.matchAll(new RegExp(TOKEN_WITH_FALLBACK.source, `g`))) {
    if (!found.includes(token)) found.push(token)
  }
  for (const [, token] of text.matchAll(new RegExp(PLAIN_TOKEN.source, `g`))) {
    if (!found.includes(token)) found.push(token)
  }
  return found
}

/** The fallback currently written against each token, keyed by token. Tokens without one are absent. */
export function readFallbacks(text: string): Record<string, string> {
  const fallbacks: Record<string, string> = {}
  for (const [, token, doubleQuoted, singleQuoted] of text.matchAll(new RegExp(TOKEN_WITH_FALLBACK.source, `g`))) {
    const raw = doubleQuoted ?? singleQuoted
    if (raw !== undefined) fallbacks[token] = unescape(raw)
  }
  return fallbacks
}

/** Rewrite `text` so each token carries the fallback given for it. An empty or missing entry strips the
 *  fallback back to a plain token, so clearing the input undoes it rather than writing `?? ""`. */
export function writeFallbacks(text: string, fallbacks: Record<string, string>): string {
  const withPlain = text.replace(new RegExp(TOKEN_WITH_FALLBACK.source, `g`), (_match, token: string) => `{{${token}}}`)
  return withPlain.replace(new RegExp(PLAIN_TOKEN.source, `g`), (_match, token: string) => {
    const fallback = fallbacks[token]
    return fallback ? `{{${token} ?? "${escape(fallback)}"}}` : `{{${token}}}`
  })
}

/** What the recipient would see. `sample` is keyed however the caller keys it; `resolve` hands back the
 *  value for a token, or undefined/empty when the contact has none. Mirrors the engine: a real value
 *  wins, otherwise the fallback, otherwise nothing at all. */
export function renderWithFallbacks(text: string, resolve: (token: string) => string | undefined): string {
  return text.replace(
    new RegExp(TOKEN_WITH_FALLBACK.source, `g`),
    (_match, token: string, doubleQuoted?: string, singleQuoted?: string) => {
      const value = resolve(token)
      if (value) return value
      const raw = doubleQuoted ?? singleQuoted
      return raw === undefined ? `` : unescape(raw)
    },
  )
}
